import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { pick } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, take, tap } from 'rxjs/operators';
import { CheckBalanceResponse } from '../../modules/common/order-entry/models/check-balance.response';
import { CheckIbanResponse, PersonaFisicaGiuridica } from '../../modules/common/order-entry/models/check-iban-response';
import { TerminationOrderService } from '../../modules/switch-in/order-entry/steps/02-dati-prodotto/ko-credit-check/termination-order.service';
import { setScoreCardStatus } from '../../store/actions/order-entry.actions';
import { MacroFlowType } from '../../store/models/flow-type';
import { OrderEntryState } from '../../store/models/order-entry-state';
import { ContactV2, Product } from '../../store/models/order-entry-state_v2';
import { EglState } from '../../store/reducers';
import {
    v2isScoreCardRequired,
    v2OrderEntryState,
    v2SelectContact,
    v2SelectDistinctCommodities,
    v2SelectFlowType,
    v2SelectMacroFlowType,
    v2SelectVisibleProducts
} from '../../store/selectors/order-entry-v2.selectors';
import { selectIsMortisCausa } from '../../store/selectors/order-entry.selectors';
import { productsFieldRetrieverGenerator } from '../../store/selectors/selector-utility.functions';
import { selectCartSegment, selectContact, selectCurrentProfile } from '../../store/selectors/user.selectors';
import { AptPaymentFrequency } from '../enums/apttus/apt-payment-frequency';
import { AptPaymentInstrument } from '../enums/apttus/apt-payment-instrument';
import { ChannelCodeInverse } from '../enums/d365/d365-channel-code';
import { D365CustomerSegment } from '../enums/d365/d365-customer-segment';
import { mapToShippingAddress } from '../functions/address.functions';
import { getTransactionId } from '../functions/misc.functions';
import { getCompleteCivic } from '../functions/string-format.functions';
import {
    containsBroadBandProduct,
    flowTypeUtil,
    hasOnlyExtraCommodities,
    isCommodityFamily
} from '../functions/verifications.functions';
import { BaseApiResponse, StatusResponse } from '../interfaces/base-api-response';
import { LegalFormType, RetrieveCreditCheckRequest, RetrieveInsolutoRequest } from '../models/app/credit-check.request';
import { EsitiData, InsolutoResponseSiebel } from '../models/app/recupera-dati-salesup.response';
import { ServiceError } from '../models/app/service-error';
import { Contact } from '../models/user/contact';
import { TelemetryMetricService } from '../services/app/telemetry-metric.service';
import { ApiService } from '../services/shared/api.service';
import { LoadingService } from '../services/shared/loading.service';
import { LoggerService } from '../services/shared/logger.service';
import { PrivateConfigurationService } from '../services/shared/private-configuration.service';
import { ApiMngApi, BaseProvider } from './base-provider';
import { CommonProvider } from './common-provider';

@Injectable({ providedIn: 'root' })
export class CreditCheckProvider extends BaseProvider {
    constructor(
        private api: ApiService,
        protected configSrv: PrivateConfigurationService,
        private store: Store<EglState>,
        private logger: LoggerService,
        private telemetrySrv: TelemetryMetricService,
        protected terminationOrderService: TerminationOrderService,
        translate: TranslateService,
        private commonProvider: CommonProvider
    ) {
        super(configSrv, translate);
    }

    /**
     * @description: credit check by CF or PIVA for specific users category
     * @param req
     */
    creditCheckFromDDL(
        req: Omit<RetrieveCreditCheckRequest, 'checkTypes'> & {
            checkTypes: ScoreCardCheckTypes[];
        }
    ): Observable<EsitiData & { esito: boolean }> {
        const CHECK_TYPES_MAP: {
            [key in ScoreCardCheckTypes]: (keyof EsitiData)[];
        } = {
            [ScoreCardCheckTypes.Credit]: ['creditCheck', 'insolutoNDS'],
            [ScoreCardCheckTypes.ScoreCard]: ['scorecard', 'insolutoNDS'],
            [ScoreCardCheckTypes.TaxCode]: ['esitoCF', 'insolutoNDS']
        };

        return this.commonProvider.getBusinessTransactionId().pipe(
            map((businessTrxId) => ({
                headers: {
                    businessTrxId
                }
            })),
            mergeMap((options) =>
                this.api.postAsync<BaseApiResponse<EsitiData>>(
                    this.getApiMngApiUrl(ApiMngApi.RetrieveDataCreditCheck),
                    {
                        ...(req || ({} as RetrieveCreditCheckRequest)),
                        checkTypes: (req?.checkTypes || []).join(',')
                    },
                    0,
                    options
                )
            ),
            tap((res) => {
                if (
                    res?.status !== StatusResponse.Success &&
                    ['DDL_009', 'DDL_010'].includes(res?.errorManagement?.errorCode)
                ) {
                    throw new Error(res?.errorManagement?.errorCode);
                }
                if (res?.status !== StatusResponse.Success || !res?.response) {
                    throw new Error(res?.errorManagement?.errorDescription || 'Credit check error.');
                }
            }),
            map((res) => res?.response),
            map((res) => {
                const responseFields: (keyof EsitiData)[] = [].concat(
                    ...req.checkTypes.map((checkType) => CHECK_TYPES_MAP[checkType])
                );
                return pick(res, responseFields);
            }),
            map((res) => ({
                ...res,
                esito: Object.values(res)
                    .map(({ esito }) => esito || esito === null)
                    .every(Boolean)
            }))
        );
    }

    /**
     * Per clienti Siebel
     * @param req
     */
    creditCheckInsoluto(req: RetrieveInsolutoRequest): Observable<BaseApiResponse<InsolutoResponseSiebel>> {
        return this.commonProvider.getBusinessTransactionId().pipe(
            map((businessTrxId) => ({
                headers: {
                    businessTrxId
                }
            })),
            mergeMap((options) =>
                this.api.postAsync<BaseApiResponse<EsitiData>>(
                    this.getApiMngApiUrl(ApiMngApi.RetrieveInsoluto),
                    req,
                    0,
                    options
                )
            ),
            catchError(() => of(null))
        );
    }

    /**
     * @description Esegue la chiamata all'API di APIManagement per la verifica della validità dell'IBAN e dell'Insoluto NDS associato
     * @param ibanVal L'iban da verificare
     * @param subjectType tipologia soggetto se PF o PG
     * @returns Promise<BaseApiResponse<CheckIbanResponse>>
     */
    ibanCheck(
        IBAN: string,
        subjectType: PersonaFisicaGiuridica,
        vatCodeOrTaxCode: string,
        provinceCode: string
    ): Observable<BaseApiResponse<CheckIbanResponse>> {
        return this.commonProvider.getBusinessTransactionId().pipe(
            map((businessTrxId) => ({
                headers: {
                    businessTrxId
                }
            })),
            mergeMap((options) =>
                this.api.postAsync<BaseApiResponse<CheckIbanResponse>>(
                    this.getApiMngApiUrl(ApiMngApi.CheckIban),
                    {
                        iban: IBAN,
                        subjectType,
                        vatRegistrationNo: vatCodeOrTaxCode,
                        provinceCode
                    },
                    0,
                    options
                )
            )
        );
    }

    scoreCard(
        checkTypes: ScoreCardCheckTypes[] = [ScoreCardCheckTypes.ScoreCard]
    ): Observable<EsitiData & { esito: boolean }> {
        return combineLatest([
            this.store.select(v2isScoreCardRequired),
            this.store.select(v2SelectFlowType),
            this.store.select(selectIsMortisCausa)
        ]).pipe(
            take(1),
            // Gestisco la casistica in cui non devo fare la ScoreCard come un errore per saltare tutto il processo
            tap(([isScoreCardRequired, flowType, isMortisCausa]) => {
                if (
                    !isScoreCardRequired ||
                    (flowTypeUtil(flowType).inMacroFlowTypes(MacroFlowType.Voltura) && !!isMortisCausa)
                ) {
                    throw null;
                }
            }),
            mergeMap(() =>
                combineLatest([
                    this.store.select(v2SelectMacroFlowType),
                    // Richiamata nel Dragon Router ed eseguita per l'item corrente nelle sotto rotte
                    this.store.select(v2SelectDistinctCommodities()),
                    this.store.select(selectCurrentProfile),
                    this.store.select(selectContact),
                    this.store.select(selectCartSegment),
                    this.store.select(v2OrderEntryState),
                    this.store.select(v2SelectVisibleProducts('ALL')),
                    this.store.select(v2SelectContact)
                ])
            ),
            take(1),
            map(
                ([
                    macroFlowType,
                    commodities,
                    currentProfile,
                    contact,
                    cartSegment,
                    { anagraficaMb, creditCheckStatus },
                    products,
                    contactV2
                ]: [
                    MacroFlowType,
                    Product[],
                    any,
                    Contact,
                    D365CustomerSegment,
                    OrderEntryState,
                    Product[],
                    ContactV2
                ]): Omit<RetrieveCreditCheckRequest, 'checkTypes'> & {
                    checkTypes: ScoreCardCheckTypes[];
                } => {
                    const commBroadBand = products.filter(
                        (prd: Product) => containsBroadBandProduct(prd?.productType) || isCommodityFamily(prd?.family)
                    );
                    const getProduct = productsFieldRetrieverGenerator(commodities);
                    const phoneNumbers = [
                        `${contact?.prefixMobilephone || ''}${contact?.mobilephone || ''}`.trim(),
                        `${contact?.prefixTelephone1 || ''}${contact?.telephone1 || ''}`.trim(),
                        `${contact?.prefixTelephone2 || ''}${contact?.telephone2 || ''}`.trim()
                    ].filter(Boolean);

                    const isMicrobusiness = cartSegment === D365CustomerSegment.Microbusiness;

                    const PAYMENT_MODE_MAP: {
                        [key in AptPaymentInstrument | string]?: string;
                    } = {
                        [AptPaymentInstrument.Bollettino]: 'Bollettino',
                        [AptPaymentInstrument.AddebitoCC]: 'Domiciliazione',
                        [AptPaymentInstrument.CartaCredito]: 'Carta di credito',
                        [AptPaymentInstrument.BonificoCessione]: 'Bonifico',
                        [AptPaymentInstrument.BonificoDetrazioneFiscale]: 'Bonifico',
                        [AptPaymentInstrument.BonificoDetrazioneFiscaleVistaUnica]: 'Bonifico',
                        ['DEFAULT']: ''
                    };

                    const OPERATING_MODE_MAP: {
                        [key in MacroFlowType | string]: string;
                    } = {
                        [MacroFlowType.SwitchIn]: 'SWITCH IN',
                        [MacroFlowType.Attivazione]: 'ATTIVAZIONE',
                        [MacroFlowType.Voltura]: 'VOLTURA',
                        [MacroFlowType.VolturaSwitchIn]: 'VOLTURA CON SWITCH',
                        [MacroFlowType.CambioProdotto]: 'CAMBIO PRODOTTO',
                        [MacroFlowType.BroadBand]: 'BROADBAND',
                        ['DEFAULT']: 'ALTRO'
                    };
                    return {
                        checkTypes,
                        ...(!isMicrobusiness && { fiscalCode: contact?.egl_taxcode }),
                        ...(isMicrobusiness && { vatNumber: contact?.egl_vatcode }),
                        customerId: contact?.egl_customercode,
                        iban: getProduct((product) => product?.paymentInfo?.paymentTool?.iban),
                        phone: phoneNumbers[0],
                        email: contact?.emailaddress1,
                        cervedVerification: {
                            subjectType: isMicrobusiness ? 'PG' : 'PF',
                            customerType:
                                (anagraficaMb?.legalForm as LegalFormType) || LegalFormType.PersonaFisicaPrivato,
                            scoreCardId: isMicrobusiness ? 'ENIPG2' : 'ENIPF2',
                            taxCode: contact.egl_taxcode,
                            surname: contact.lastname,
                            name: contact.firstname,
                            companyName: isMicrobusiness ? anagraficaMb?.companyName : null,
                            iban: getProduct((product) => product?.paymentInfo?.paymentTool?.iban),
                            customerTransactionId: getTransactionId(),
                            paymentScore: creditCheckStatus?.ccDetails?.paymentScore,
                            isCustomer: {
                                isCustomerFlag: contact?.isCustomer,
                                internalId: contact?.isCustomer ? contact?.egl_customercode : null,
                                startActivationDate: contact?.isCustomer ? contact?.startActivation : null
                            },
                            isFormerCustomer: {
                                isFormerCustomerFlag: contact?.isFormerCustomer,
                                ceaseReasonCode: creditCheckStatus?.ccDetails?.ceaseReasonCode
                            },
                            acquisitionAgency: {
                                codeAgency: currentProfile?.selectedVirtualAg?.DefaultAgencyBranch?.Code,
                                descAgency: currentProfile?.selectedVirtualAg?.DefaultAgencyBranch?.Name
                            },
                            aquisitionChannel: currentProfile?.channelCode
                                ? ChannelCodeInverse[currentProfile?.channelCode]
                                : null,
                            operatingMode: OPERATING_MODE_MAP[macroFlowType || 'DEFAULT'],
                            checkType: 'SCORECARD',
                            customerContactInfo: {
                                email: contact?.emailaddress1,
                                phoneNumbers,
                                shippingAddress: mapToShippingAddress(
                                    typeof contactV2?.mainAddress !== 'boolean' ? contactV2?.mainAddress : null
                                )
                            },
                            supplyPointsInfo: commBroadBand.map((prd) => ({
                                type: prd?.powerOrGas || prd?.broadBandType,
                                usageValue: prd?.technicalDetails?.consumption || 0,
                                supplyAddress: {
                                    postCode: prd.deliveryAddress.cap,
                                    provinceCode: prd.deliveryAddress.shortProvince,
                                    dug: prd.deliveryAddress.toponym,
                                    street: prd.deliveryAddress.street,
                                    number: getCompleteCivic(prd.deliveryAddress),
                                    municipality: {
                                        name: prd?.deliveryAddress.municipality
                                    }
                                },
                                pointInfoPaymentMode: {
                                    paymentMode: PAYMENT_MODE_MAP[prd?.paymentInfo?.paymentInstrument || 'DEFAULT']
                                }
                            })) as [],
                            productsInfo:
                                products
                                    .filter((prd) => hasOnlyExtraCommodities([prd]))
                                    ?.map((product: Product) => ({
                                        productFamily: product?.family,
                                        paymentInstrument: product?.paymentInfo?.paymentInstrument,
                                        paymentType: product?.paymentInfo?.paymentType,
                                        productType: product?.productType,
                                        productFamilyAddress: mapToShippingAddress(product?.deliveryAddress),
                                        productFamilyPaymentMode: {
                                            paymentFee: (
                                                Number(product?.prices?.netPrice) - (product?.paymentAdvance || 0)
                                            )?.toString(),
                                            paymentFrequency:
                                                product?.paymentInfo?.paymentFrequency ===
                                                AptPaymentFrequency.UnicaSoluzione
                                                    ? 0
                                                    : product?.paymentInfo?.paymentFrequency ===
                                                        AptPaymentFrequency.Mensile
                                                      ? 1
                                                      : 2,
                                            paymentMode:
                                                PAYMENT_MODE_MAP[
                                                    product?.paymentInfo?.paymentInstrument || 'DEFAULT'
                                                ] || '',
                                            rateNumber: Number(product?.configurations?.installmentDuration) || 1
                                        },
                                        requestsNumber: 1
                                    })) || []
                        }
                    };
                }
            ),
            mergeMap((data) => this.creditCheckFromDDL(data)),
            catchError((err) => {
                if (err === null) {
                    return of(null);
                }
                throw err;
            })
        );
    }

    public orderEntryScoreCard(checkTypes?: ScoreCardCheckTypes[]): Observable<boolean> {
        return this.scoreCard(checkTypes).pipe(
            this.telemetrySrv.rxTelemetry('Performance: End Call Scorecard service'),
            mergeMap((esiti) =>
                this.store.select(selectContact).pipe(
                    take(1),
                    map((contact) => ({
                        esiti,
                        customerData: {
                            cfPiva: contact?.egl_vatcode || contact?.egl_taxcode,
                            firstname: contact?.firstname || null,
                            lastnameOrName: contact?.lastname || contact?.name
                        }
                    }))
                )
            ),
            mergeMap(({ esiti, customerData }) =>
                esiti ? this.terminationOrderService.manageCheckDdlResult(esiti, customerData) : of(null)
            ),
            tap((res) => {
                // Gestisco il OK logico, verifica creditizia superata
                if (res?.esito) {
                    this.logger.warn(
                        `Esito Scorecard positivo (esito = true). Faccio proseguire con descrizioneEsito = ${res?.scorecard?.dettaglio}`
                    );

                    this.store.dispatch(
                        setScoreCardStatus({
                            s: res?.scorecard?.dettaglio
                        })
                    );
                } else {
                    // Gestisco la casistica in cui salto la creditCheck
                    this.logger.warn(
                        'Skip chiamata al nuovo servizio scorecard perchè non richiesta dal check dbCredito'
                    );
                }
            }),
            map(() => true),
            // Gestisco il KO logico, verifica creditizia non superata
            catchError((err: ServiceError) => {
                this.logger.warn(
                    `Chiamata a scorecard in errore - ${err?.message} - Gestisco come un caso di RETRY e faccio proseguire`
                );
                // Gestisco gli errori tecnici
                if (!ServiceError.isServiceError(err)) {
                    this.store.dispatch(
                        setScoreCardStatus({
                            s: 'NON DISPONIBILE'
                        })
                    );
                    return of(null);
                }
                this.logger.warn(
                    `Esito Scorecard negativo (false). ${
                        err?.code === 'CC_EXTRACOMMODITY'
                            ? 'Apro modale di suggerimento cambio PaymentInstrument'
                            : 'Navigo verso la pagina di KO Credit Check'
                    } `
                );
                this.logger.toastError(
                    'Non è possibile proseguire con la vendita per esito negativo sulla verifica creditizia',
                    'Attenzione',
                    true
                );
                throw err;
            }),
            LoadingService.loaderOperator('Verifica creditizia')
        );
    }

    checkBalance(
        cartAmount: number,
        accountNumber: string,
        cartProducts: any[]
    ): Observable<BaseApiResponse<CheckBalanceResponse>> {
        return this.commonProvider.getBusinessTransactionId().pipe(
            mergeMap((businessTrxId) =>
                this.api.postAsync<BaseApiResponse<CheckBalanceResponse>>(
                    this.getApiMngApiUrl(ApiMngApi.CheckBalance),
                    {
                        channel: 'SUP',
                        clientCode: accountNumber,
                        totalCartPayment: cartAmount,
                        cartProducts
                    },
                    0,
                    {
                        headers: {
                            businessTrxId
                        }
                    }
                )
            )
        );
    }
}

export enum ScoreCardCheckTypes {
    TaxCode = 'CF',
    Credit = 'CREDIT',
    ScoreCard = 'SCORECARD'
}
